import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchSchemeList, deleteScheme } from "../api";
import { usePermissions } from "../../../hooks/usePermissions";
import AddSchemeModel from "../pages/AddSchemeModel";
import { message } from "antd";
import { Popconfirm } from "antd";
import { ActionType, ModuleType, PermissionButton } from "components";

import {
  Table,
  Input,
  Button,
  Space,
  Typography,
  Spin,
  Pagination,
  Row,
  Col,
  Grid,
  Card,
  Tag,
  Tooltip,
  theme as antdTheme,
} from "antd";
import { SearchOutlined } from "@ant-design/icons";
import "bootstrap/dist/css/bootstrap.min.css";

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const SchemeList = () => {
  const {
    canViewField,
    canEditField,
    canViewModule,
    canViewCreateModule,
  } = usePermissions();

  const module = ModuleType.SCHEME;

  const canViewName = canViewField('scheme', 'name');
  const canViewCode = canViewField('scheme', 'code');
  const canViewRate = canViewField('scheme', 'ratePerGram');
  const canViewStatus = canViewField('scheme', 'status');

  const [SchemeList, setSchemeList] = useState([]); //Variable
  const [searchTerm, setSearchTerm] = useState(""); //Variable
  const [searchQuery, setSearchQuery] = useState(""); //Variable
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingScheme, setEditingScheme] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10, // for now after testing change to default : 10
    total: 0,
  });

  const navigate = useNavigate();
  const { token } = antdTheme.useToken();
  const screens = useBreakpoint();

  const [messageApi, contextHolder] = message.useMessage(); //Need this as well to show message

  //Load during the page initial
  useEffect(() => {
    loadSchemeList();
  }, [pagination.current, pagination.pageSize, searchQuery]);

  const loadSchemeList = async () => {
    setLoading(true);
    try {
      const response = await fetchSchemeList({
        search: searchQuery,
        page: pagination.current,
        limit: pagination.pageSize,
      });

      const { data, totalCount } = response.data;

      setSchemeList(data || []);
      setPagination((prev) => ({
        ...prev,
        total: totalCount || 0,
      }));
      setLoading(false);
      console.log(
        "Fetch Schemel List response: " + JSON.stringify(response.data)
      );
    } catch (error) {
      setLoading(false);
      console.log("getSchemeList error " + error);
      messageApi.error('Failed to load schemes');
    }
  };

  const handleSearch = (value) => {
    setPagination((prev) => ({ ...prev, current: 1 })); // reset to first page
    setSearchQuery(value);
  };

  const handleAddSchemeClick = () => {
    setEditingScheme(null);
    setIsModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingScheme(record);
    setIsModalVisible(true); // open modal with scheme data
  };

  const handleDelete = async (id) => {
    try {
      const response = await deleteScheme({ id: id });
      loadSchemeList(); // refresh table
      messageApi.success("Succesfully Deleted Scheme");
    } catch (error) {
      console.error("Delete error:", error);
      messageApi.error("Failed to delete scheme");
    }
  };

  const handleModalClose = (shouldReload = false) => {
    setIsModalVisible(false);
    if (shouldReload) loadSchemeList(); // 👈 reload list after adding
  };

  const columns = [
    canViewName && {
      title: "Scheme Name",
      dataIndex: "name",
      key: "name",
      responsive: ["xs", "sm", "md", "lg"],
    },
    canViewCode && {
      title: "Scheme Code",
      dataIndex: "code",
      key: "code",
      responsive: ["sm", "md", "lg"],
    },
    canViewRate && {
      title: "Rate Per/Gram",
      dataIndex: "ratePerGram",
      key: "ratePerGram",
      responsive: ["md", "lg"],
    },
    canViewStatus && {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={status === 'active' ? 'green' : 'default'} style={{ textTransform: 'capitalize' }}>
          {status}
        </Tag>
      ),
      responsive: ["sm", "md", "lg"],
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      responsive: ["md", "lg"],
    },
    {
      title: " ",
      key: "actions",
      render: (_, record) => (
        <Space>
          <PermissionButton
            buttonType={ActionType.EDIT}
            module={module}
            onClick={() => handleEdit(record)}
            size={screens.xs ? 'small' : 'middle'}
          >
            Edit
          </PermissionButton>
          <Popconfirm
            title="Are you sure you want to delete this scheme?"
            onConfirm={() => handleDelete(record._id)}
            okText="Yes"
            cancelText="No"
          >
            <PermissionButton
              buttonType={ActionType.DELETE}
              module={module}
              danger
              size={screens.xs ? 'small' : 'middle'}
            >
              Delete
            </PermissionButton>
          </Popconfirm>
        </Space>
      ),
      responsive: ["xs", "sm", "md", "lg"],
    },
  ].filter(Boolean);

  if (!canViewModule(module)) {
    return (
      <div style={{ textAlign: "center", marginTop: 60 }}>
        <Text type="danger">You do not have permission to view Schemes.</Text>
      </div>
    );
  }

  return (
    <>
      {contextHolder}
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <Card>
              <Row justify="space-between" align="middle" className="mb-4">
                <Col>
                  <Title level={2} style={{ margin: 0 }}>
                    Schemes
                  </Title>
                </Col>
                <Col>
                  {canViewCreateModule(module) && (
                    <PermissionButton
                      type="primary"
                      buttonType={ActionType.CREATE}
                      module={module}
                      onClick={handleAddSchemeClick}
                      size={screens.xs ? 'small' : 'middle'}
                    >
                      Add Scheme
                    </PermissionButton>
                  )}
                </Col>
              </Row>

              {/* Search Row */}
              <Row className="mb-3">
                <Col xs={24} sm={18} md={20}>
                  <Input.Search
                    placeholder="Search by Scheme Name"
                    enterButton={<SearchOutlined />}
                    allowClear
                    onSearch={handleSearch}
                    size={screens.xs ? 'small' : 'middle'}
                  />
                </Col>
              </Row>

              <Spin spinning={loading}>
                {screens.md ? (
                  <Table
                    columns={columns}
                    dataSource={SchemeList}
                    pagination={false}
                    rowKey={(record) => record._id || record.name}
                    scroll={{ x: 'max-content' }}
                    size={screens.lg ? 'middle' : 'small'}
                  />
                ) : (
                  <div>
                    {SchemeList && SchemeList.length ? (
                      SchemeList.map((s) => (
                        <Card key={s._id}
                          size="small"
                          hoverable
                          style={{ marginBottom: 12 }}
                          onClick={() => handleEdit(s)}
                        >
                          <Row justify="space-between" align="middle">
                            <Col span={16}>
                              <Title level={5} style={{ marginBottom: 0 }}>{s.name || '-'}</Title>
                              <Text type="secondary" style={{ display: 'block' }}>{s.code || '-'}</Text>
                            </Col>
                            <Col span={8} style={{ textAlign: 'right' }}>
                              <Tag color={s.status === 'active' ? 'green' : 'default'}>{s.status || '-'}</Tag>
                              <div style={{ marginTop: 4 }}>
                                <Text type="secondary">{s.ratePerGram || '-'}</Text>
                              </div>
                            </Col>
                          </Row>
                          <div style={{ marginTop: 8, textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                            <Space>
                              <PermissionButton buttonType={ActionType.EDIT} module={module} size="small" onClick={() => handleEdit(s)}>Edit</PermissionButton>
                              <Popconfirm title="Delete Scheme" onConfirm={() => handleDelete(s._id)}>
                                <PermissionButton buttonType={ActionType.DELETE} module={module} danger size="small">Delete</PermissionButton>
                              </Popconfirm>
                            </Space>
                          </div>
                        </Card>
                      ))
                    ) : (
                      <div style={{ textAlign: 'center', padding: 24 }}>
                        <Text type="secondary">No schemes found.</Text>
                      </div>
                    )}
                  </div>
                )}

                {/* Pagination */}
                {pagination.total > 0 && (
                  <Row justify="center" className="mt-4">
                    <Col>
                      <Pagination
                        current={pagination.current}
                        pageSize={pagination.pageSize}
                        total={pagination.total}
                        onChange={(page, pageSize) =>
                          setPagination((prev) => ({
                            ...prev,
                            current: page,
                            pageSize: pageSize,
                          }))
                        }
                        showSizeChanger
                        showQuickJumper={!screens.xs}
                        showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} schemes`}
                        size={screens.xs ? 'small' : 'default'}
                        responsive
                      />
                    </Col>
                  </Row>
                )}

                {/* Modal Component */}
                <AddSchemeModel
                  visible={isModalVisible}
                  onClose={handleModalClose}
                  schemeToEdit={editingScheme}
                />
              </Spin>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
};

export default SchemeList;